import React, { useState, useEffect } from 'react'
import { getNBAScorecard, getNBAValidation, getNBAMeta } from './api'
import { shareBthRecord } from './share'

// Public NBA track-record page — the shareable proof link (/nba/proof). Bypasses the
// age + access gates and reads only aggregate, settled data: the live season scorecard
// (record + per-market hit %) and the backtest validation scorecard (per-market
// calibration + verdict). No per-play data, no picks — just the receipts.

const T = {
  bg: '#05090f', card: '#0F1520', card2: '#0b1119', border: '#22303f',
  text: '#eaf3ec', muted: '#8ea3b0', faint: '#5f7382', green: '#00D97E', green2: '#2fe37a',
  amber: '#e3c02f', red: '#ff7a7a', orange: '#ff8a3d',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
}
const pctInt = p => (p == null ? '—' : `${Math.round(p * 100)}%`)
const LABEL = { points: 'Points', rebounds: 'Rebounds', assists: 'Assists',
  threes: 'Threes Made', pra: 'Pts+Reb+Ast' }
const VERDICT_COLOR = { PASS: T.green2, REVIEW: T.amber, BUILDING: T.faint }

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 22, color: color || T.text }}>{value}</div>
    </div>
  )
}

export default function NBAProofPage() {
  // Season follows the backend (via /nba/meta) so the annual bump is env-only.
  const [season, setSeason] = useState(2026)
  const [card, setCard] = useState(null)
  const [val, setVal] = useState(null)
  const [rec, setRec] = useState(null)
  useEffect(() => { getNBAMeta().then(mt => { if (mt?.season) setSeason(mt.season); setRec(mt?.record || { public: false }) }) }, [])
  useEffect(() => {
    getNBAScorecard(season).then(setCard)
    getNBAValidation().then(setVal)
  }, [season])

  const b = rec?.built_to_hit || {}
  const wl = b.graded ? `${b.hits}-${b.misses}` : '—'
  const byProp = b.by_prop || {}
  const ex = rec?.experimental || {}
  const units = u => u == null ? '—' : `${u >= 0 ? '+' : ''}${u.toFixed(2)}u`
  const markets = val?.markets || {}
  const po = card?.priced?.overall || {}

  let override = false
  try { override = new URLSearchParams(window.location.search).get('showrecord') === '1' } catch (_) {}
  if (rec && !rec.public && !override) return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
      <div style={{ maxWidth: 380 }}>
        <div style={{ fontSize: 32 }}>🧪</div>
        <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>LINEBRKR NBA — record in testing</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
          Every read is graded on the box score. The public record opens after {rec.nights_needed || 7} graded
          nights ({rec.nights_graded || 0} so far).</div>
      </div>
    </div>
  )
  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text,
      fontFamily: 'system-ui, -apple-system, sans-serif', padding: '0 0 48px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.03em' }}>
            LINE<span style={{ color: T.green2 }}>BRKR</span>
          </span>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', color: '#1a0d04',
            background: T.orange, borderRadius: 6, padding: '3px 7px' }}>NBA · PROOF</span>
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 18 }}>
          Built to hit — each player's best "N+" read, graded on the box score. Research, not betting advice · 21+
        </div>

        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
          padding: '16px 18px', display: 'flex', gap: 26, marginBottom: 16, flexWrap: 'wrap' }}>
          <Stat label={`${season} Record`} value={wl} />
          <Stat label="Hit %" value={pctInt(b.hit_rate)} color={T.green} />
          <Stat label="Model said" value={pctInt(b.expected)} />
          {rec?.replay_hit_rate != null && <Stat label="2025 replay" value={pctInt(rec.replay_hit_rate)} />}
        </div>
        {b.graded > 0 && <button onClick={() => shareBthRecord(rec, season)} style={{ width: '100%', cursor: 'pointer',
          fontSize: 14, fontWeight: 800, color: T.green2, background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: '12px 0', marginBottom: 16 }}>⤴ Share the record graphic</button>}

        {Object.keys(byProp).length > 0 && (
          <>
            <SectionTitle>Built to hit, by stat</SectionTitle>
            <Table rows={Object.entries(byProp).map(([k, v]) => ({
              label: LABEL[k] || k, a: v.hits != null ? `${v.hits}-${v.misses}` : (v.graded || '—'),
              b: pctInt(v.hit_rate), bColor: T.green,
            }))} colA="W-L" colB="Hit %" />
          </>
        )}

        {(ex.news?.graded > 0 || ex.snipes?.graded > 0) && <>
          <SectionTitle>Experimental (not in the headline)</SectionTitle>
          <Table rows={[
            ...(ex.news?.graded ? [{ label: 'News edges', a: `${ex.news.wins}-${ex.news.losses}`, b: units(ex.news.units) }] : []),
            ...(ex.snipes?.graded ? [{ label: 'Snipes (+300 and longer)', a: `${ex.snipes.hits}/${ex.snipes.graded}`, b: units(ex.snipes.units) }] : []),
          ]} colA="Record" colB="Units" />
        </>}

        {(po.wins || po.losses) ? <>
          <SectionTitle>Model board vs book lines (research)</SectionTitle>
          <div style={{ fontSize: 12, color: T.faint, marginBottom: 8, lineHeight: 1.5 }}>
            The 50/50-line board, priced at the posted odds: {po.wins}-{po.losses}
            {po.roi != null && <> · {po.roi >= 0 ? '+' : ''}{(po.roi * 100).toFixed(1)}% ROI</>}. It stays research until
            it beats the closing line on a real sample.</div>
        </> : null}

        <SectionTitle>Backtest validation {val?.seasons?.length ? `(${val.seasons.join(', ')})` : ''}</SectionTitle>
        <div style={{ fontSize: 12, color: T.faint, marginBottom: 8, lineHeight: 1.5 }}>
          Each market replayed day-by-day on completed seasons (walk-forward, no leakage).
          Calibration error = how far predicted % sat from actual. Verdict: PASS &lt; 5pp · REVIEW · BUILDING (thin).
        </div>
        {Object.keys(markets).length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13, padding: '18px 4px' }}>
            No backtest on record yet — connect the data feed and run validation.
          </div>
        ) : (
          <Table rows={Object.entries(markets).map(([k, c]) => ({
            label: LABEL[k] || k,
            a: c.plays ? `${c.hit_rate}%` : '—',
            b: c.cal_err != null ? `${c.cal_err}pp` : '—',
            c: c.verdict, cColor: VERDICT_COLOR[c.verdict] || T.faint,
            sub: `${c.plays || 0} plays`,
          }))} colA="Hit %" colB="Cal err" colC="Verdict" />
        )}

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <a href="/?sport=nba" style={{ fontSize: 13, fontWeight: 700, color: T.green2, textDecoration: 'none' }}>
            See tonight's board →
          </a>
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 13, fontWeight: 800, color: T.text, margin: '18px 0 8px',
    letterSpacing: '-0.01em' }}>{children}</div>
}

function Table({ rows, colA, colB, colC }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', padding: '8px 14px', fontSize: 10, color: T.faint,
        textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `1px solid ${T.border}` }}>
        <span style={{ flex: 1 }}>Market</span>
        <span style={{ width: 64, textAlign: 'right' }}>{colA}</span>
        <span style={{ width: 64, textAlign: 'right' }}>{colB}</span>
        {colC && <span style={{ width: 78, textAlign: 'right' }}>{colC}</span>}
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px',
          borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <span style={{ flex: 1 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.label}</span>
            {r.sub && <span style={{ fontSize: 10.5, color: T.faint, marginLeft: 6 }}>{r.sub}</span>}
          </span>
          <span style={{ width: 64, textAlign: 'right', fontFamily: T.mono, fontSize: 13,
            color: r.bColor && colB === 'Hit %' ? r.bColor : T.text }}>{r.a}</span>
          <span style={{ width: 64, textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: T.muted }}>{r.b}</span>
          {colC && <span style={{ width: 78, textAlign: 'right', fontSize: 11, fontWeight: 800,
            color: r.cColor }}>{r.c}</span>}
        </div>
      ))}
    </div>
  )
}
